import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, TrendingDown, Clock, Server, Lock } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

export const DualOfferSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Subtle gradient divider */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-[rgba(255,255,255,0.1)] to-transparent" />
      
      <div className="container-professional" ref={elementRef} style={getRevealStyle()}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-primary font-mono mb-4">
            Two Ways to Work With Us
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Choose Your Path
          </h2>
          <p className="text-[rgba(255,255,255,0.5)] max-w-xl mx-auto">
            Whether you're scaling a startup or running a local business, we have the right approach.
          </p>
        </div>

        {/* Two-Column Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          
          {/* Enterprise Engineering Card */}
          <div className="group relative bg-[#111111] rounded-2xl border border-[rgba(255,255,255,0.08)] p-8 lg:p-10 hover:border-primary/40 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_50px_-15px_hsl(190_100%_50%_/_0.15)]">
            {/* Glow effect on hover */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              {/* Icon and Tag */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <Zap className="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-300" />
                </div>
                <span className="text-xs uppercase tracking-wider text-primary font-mono">
                  For Scale-ups & Enterprises
                </span>
              </div>

              {/* Title */}
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Enterprise Engineering
              </h3>

              {/* Description */}
              <p className="text-[rgba(255,255,255,0.6)] mb-8 leading-relaxed">
                Custom web apps, mobile apps, and AI integration. Built for scale, designed for speed.
              </p>

              {/* Key Points */}
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-[rgba(255,255,255,0.8)]">
                    <span className="text-white font-medium">MVPs in 3-6 months</span> — competitors take 12+
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Server className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-[rgba(255,255,255,0.8)]">
                    AI integration and custom operations
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingDown className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-[rgba(255,255,255,0.8)]">
                    High ROI, measurable results
                  </span>
                </li>
              </ul>

              {/* Case Study Teaser */}
              <div className="relative bg-[rgba(255,255,255,0.03)] rounded-xl p-4 mb-8 border border-[rgba(255,255,255,0.05)] overflow-hidden group-hover:border-primary/20 transition-colors duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center gap-3">
                  <span className="text-3xl font-bold text-primary">32%</span>
                  <div>
                    <p className="text-white text-sm font-medium">Fuel Cost Reduction</p>
                    <p className="text-[rgba(255,255,255,0.5)] text-xs">Logistics client, AI route optimization</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Link 
                to="/services" 
                className="inline-flex items-center gap-2 text-primary font-medium group/link"
              >
                Explore Enterprise Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Business Essentials Card */}
          <div className="group relative bg-[#111111] rounded-2xl border border-[rgba(255,255,255,0.08)] p-8 lg:p-10 hover:border-emerald-500/40 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_50px_-15px_rgba(16,185,129,0.15)]">
            {/* Glow effect on hover */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 via-emerald-500/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              {/* Icon and Tag */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors duration-300">
                  <Shield className="w-6 h-6 text-emerald-500 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <span className="text-xs uppercase tracking-wider text-emerald-500 font-mono">
                  For Small Businesses
                </span>
              </div>

              {/* Title */}
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Business Essentials
              </h3>

              {/* Description */}
              <p className="text-[rgba(255,255,255,0.6)] mb-8 leading-relaxed">
                Managed websites with everything included. Focus on your business, not your tech.
              </p>

              {/* Key Points */}
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center mt-0.5 shrink-0">
                    <span className="text-emerald-500 text-xs font-bold">$</span>
                  </span>
                  <span className="text-[rgba(255,255,255,0.8)]">
                    <span className="text-white font-medium">$500/year</span> — everything included
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Server className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-[rgba(255,255,255,0.8)]">
                    Hosting, SSL, and updates managed
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-[rgba(255,255,255,0.8)]">
                    Security monitoring included
                  </span>
                </li>
              </ul>

              {/* Peace of Mind Box */}
              <div className="bg-[rgba(255,255,255,0.03)] rounded-xl p-4 mb-8 border border-[rgba(255,255,255,0.05)]">
                <p className="text-white text-sm font-medium mb-1">Managed Peace of Mind</p>
                <p className="text-[rgba(255,255,255,0.5)] text-xs">
                  Stop worrying about hosting, security, or updates. We handle it all.
                </p>
              </div>

              {/* CTA */}
              <Link 
                to="/pricing" 
                className="inline-flex items-center gap-2 text-emerald-500 font-medium group/link"
              >
                See Essentials Plans
                <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
